/**
 * Fusebox panel — the floor's power controls, opened by pressing E at the
 * fusebox (or the `panel` dev command). One breaker per room switches all of
 * that room's lights on/off. (Cameras / turrets / doors will slot in later.)
 *
 * KEYBOARD-FIRST (the game is keyboard-only): while it's open it's a modal menu
 * — ↑/↓ move the selection cursor, Space/Enter flips the highlighted breaker,
 * E/Escape closes it. Mouse clicks still work as a convenience. This is the
 * template for every in-game menu: open → arrows select → Space activates →
 * E/Escape closes, with the game input suspended meanwhile.
 *
 * No PixiJS.
 */

import type { Room } from '../sim/map';

export class FuseboxPanel {
  readonly root: HTMLElement;
  private readonly listEl: HTMLElement;
  private readonly onToggle: (roomId: number) => void;
  private open = false;
  private built = false;
  private readonly rowByRoom = new Map<number, HTMLElement>();
  /** Index of the keyboard-selected breaker (into the rooms array). */
  private selected = 0;

  constructor(host: HTMLElement, onToggle: (roomId: number) => void) {
    this.onToggle = onToggle;
    this.root = document.createElement('div');
    this.root.className = 'fusebox';
    this.root.style.display = 'none';
    this.root.innerHTML = `
      <div class="fusebox-title">▦ FLOOR FUSEBOX</div>
      <div class="fusebox-sub">POWER · LIGHTING</div>
      <div class="fusebox-list"></div>
      <div class="fusebox-hint">↑↓ select · Space flip · E close</div>`;
    this.listEl = this.root.querySelector('.fusebox-list') as HTMLElement;
    host.appendChild(this.root);
  }

  isOpen(): boolean {
    return this.open;
  }

  /** Move the keyboard selection cursor (wraps). dir = -1 up, +1 down. */
  moveSelection(rooms: readonly Room[], dir: -1 | 1): void {
    if (rooms.length === 0) return;
    this.selected = (this.selected + dir + rooms.length) % rooms.length;
    this.update(rooms);
  }

  /** Flip the currently-selected breaker (Space/Enter). */
  activateSelected(rooms: readonly Room[]): void {
    const r = rooms[this.selected];
    if (!r) return;
    this.onToggle(r.id);
    this.update(rooms); // reflect the new on/off state immediately
  }

  /** Build one breaker row per room (once), then refresh states. */
  private build(rooms: readonly Room[]): void {
    this.listEl.innerHTML = '';
    this.rowByRoom.clear();
    rooms.forEach((r, i) => {
      // A div (not a <button>) so it isn't keyboard-focusable — Space/Enter is
      // handled by the modal menu, not by native button activation (which would
      // double-toggle). Mouse click still works via the listener below.
      const row = document.createElement('div');
      row.className = 'fusebox-breaker';
      row.innerHTML = `<span class="fb-name">${r.name}</span><span class="fb-switch"><span class="fb-state"></span></span>`;
      // Mouse is a convenience; it also moves the keyboard cursor to that row.
      row.addEventListener('click', () => { this.selected = i; this.onToggle(r.id); this.update(rooms); });
      this.listEl.appendChild(row);
      this.rowByRoom.set(r.id, row);
    });
    this.built = true;
  }

  /** Reflect current on/off states + the keyboard cursor (call each frame). */
  update(rooms: readonly Room[]): void {
    if (!this.built || this.rowByRoom.size !== rooms.length) this.build(rooms);
    if (this.selected >= rooms.length) this.selected = Math.max(0, rooms.length - 1);
    for (let i = 0; i < rooms.length; i++) {
      const r = rooms[i]!;
      const row = this.rowByRoom.get(r.id);
      if (!row) continue;
      row.classList.toggle('fb-on', r.lightsOn);
      row.classList.toggle('fb-selected', i === this.selected);
      const state = row.querySelector('.fb-state') as HTMLElement;
      if (state) state.textContent = r.lightsOn ? 'ON' : 'OFF';
    }
  }

  show(rooms: readonly Room[]): void {
    this.open = true;
    this.selected = 0; // start the cursor at the top breaker each open
    this.root.style.display = 'block';
    this.update(rooms);
  }

  hide(): void {
    this.open = false;
    this.root.style.display = 'none';
  }

  toggle(rooms: readonly Room[]): void {
    if (this.open) this.hide();
    else this.show(rooms);
  }
}
